// src/components/customer-search/ConfigurableFilterService.ts

import { ElementType } from "react";
import {
  NotificationsActive as AlertIcon,
  TrendingDown as DecreaseIcon,
  FlashOn as EnergyIcon,
  PriorityHigh as ImportantIcon,
  TrendingUp as IncreaseIcon,
  Star as PriorityIcon,
  Star as StarIcon,
  Highlight as HighlightIcon,
  Person as PersonIcon,
  Today as TodayIcon,
  FilterAlt as FilterIcon,
  Settings as SettingsIcon,
  FormatSize as SizeIcon,
  AccountTree as JourneyIcon
} from '@mui/icons-material';

import {
  ActivityFilter,
  ConsumptionFilter,
  JourneyTypeFilter,
  SegmentFilter,
  SegmentImportanceFilter,
  SegmentSizeFilter,
  ActionFilter
} from './FilterButton';

// Define a map of icon names to component references
export const ICON_MAP: Record<string, ElementType> = {
  AlertIcon,
  DecreaseIcon,
  EnergyIcon,
  ImportantIcon,
  IncreaseIcon,
  PriorityIcon,
  StarIcon,
  HighlightIcon,
  PersonIcon,
  TodayIcon,
  FilterIcon,
  SettingsIcon,
  SizeIcon,
  JourneyIcon
};

// Extended filter types
export interface FilterDefinition {
  id: string;
  label: string;
  iconName: string; // Use string reference to icon
  description: string;
  filters: {
    segmentFilter?: SegmentFilter;
    segmentImportanceFilter?: SegmentImportanceFilter;
    segmentSizeFilter?: SegmentSizeFilter;
    journeyTypeFilter?: JourneyTypeFilter;
    consumptionFilter?: ConsumptionFilter; // Uppdaterad typ
    activityFilter?: ActivityFilter;
    actionFilter?: ActionFilter;
    searchQuery?: string;
  };
  // Optional custom display settings
  display?: {
    order?: number;
    visibility?: boolean;
    category?: string;
  };
}

// Default filter set
export const DEFAULT_CUSTOMER_FILTERS: FilterDefinition[] = [
  {
    id: 'a-priority-next-action',
    label: 'A-kunder med nästa aktivitet',
    iconName: 'PriorityIcon',
    description: 'Visar alla A-prioritetskunder som har en schemalagd nästa aktivitet',
    filters: {
      segmentImportanceFilter: 'A',
      actionFilter: 'has-next-action'
    },
    display: {
      order: 10,
      visibility: true,
      category: 'general'
    }
  },
  {
    id: 'high-consumption-inactive',
    label: 'Hög förbrukning utan aktivitet',
    iconName: 'EnergyIcon',
    description: 'Kunder med hög förbrukning som inte har någon nylig aktivitet',
    filters: {
      consumptionFilter: 'high',
      activityFilter: 'no-contact'
    },
    display: {
      order: 20,
      visibility: true,
      category: 'general'
    }
  },
  {
    id: 'a1-customers',
    label: 'A1-kunder (Top-tier)',
    iconName: 'ImportantIcon',
    description: 'Visa endast A1-kunder (Högprioriterade, Stora)',
    filters: {
      segmentFilter: 'A1'
    },
    display: {
      order: 30,
      visibility: true,
      category: 'segments'
    }
  },
  {
    id: 'b-customers-not-logged-in',
    label: 'B-kunder som inte loggat in',
    iconName: 'AlertIcon',
    description: 'B-prioritetskunder som aldrig har loggat in',
    filters: {
      segmentImportanceFilter: 'B',
      activityFilter: 'no-login'
    },
    display: {
      order: 40,
      visibility: true,
      category: 'segments'
    }
  },
  {
    id: 'large-inactive',
    label: 'Stora kunder utan aktivitet',
    iconName: 'DecreaseIcon',
    description: 'Stora kunder (storlek 1) utan nylig aktivitet',
    filters: {
      segmentSizeFilter: '1',
      activityFilter: 'no-contact'
    },
    display: {
      order: 50,
      visibility: true,
      category: 'activity'
    }
  }
];

// Specialized filter set for Prospect/Suspect view
export const PROSPECT_SUSPECT_FILTERS: FilterDefinition[] = [
  {
    id: 'hot-leads',
    label: 'Heta leads',
    iconName: 'StarIcon',
    description: 'Leads med hög potential som behöver kontaktas',
    filters: {
      journeyTypeFilter: 'Lead',
      actionFilter: 'no-next-action'
    },
    display: {
      order: 10,
      visibility: true,
      category: 'prospects'
    }
  },
  {
    id: 'prospects-no-contact',
    label: 'Prospects utan kontakt',
    iconName: 'AlertIcon',
    description: 'Prospects som aldrig har kontaktats',
    filters: {
      journeyTypeFilter: 'Prospect',
      activityFilter: 'no-contact'
    },
    display: {
      order: 20,
      visibility: true,
      category: 'prospects'
    }
  },
  {
    id: 'recent-suspects',
    label: 'Nya suspects',
    iconName: 'TodayIcon',
    description: 'Suspects som lagts till under de senaste 30 dagarna',
    filters: {
      journeyTypeFilter: 'Suspect',
    },
    display: {
      order: 30,
      visibility: true,
      category: 'suspects'
    }
  },
  {
    id: 'vip-prospects',
    label: 'VIP Prospects',
    iconName: 'HighlightIcon',
    description: 'Prospects med VIP-status',
    filters: {
      journeyTypeFilter: 'Prospect',
    },
    display: {
      order: 40,
      visibility: true,
      category: 'prospects'
    }
  },
  {
    id: 'my-assigned-leads',
    label: 'Mina tilldelade leads',
    iconName: 'PersonIcon',
    description: 'Leads som är tilldelade till mig',
    filters: {
      journeyTypeFilter: 'Lead',
    },
    display: {
      order: 50,
      visibility: true,
      category: 'prospects'
    }
  }
];

// Custom filter type for configuration loaded from external sources
export interface FilterConfiguration {
  filters: FilterDefinition[];
  categories?: {
    id: string;
    label: string;
    order: number;
  }[];
}

// Filter service singleton class
export class FilterService {
  private static instance: FilterService;
  private userConfig: FilterConfiguration | null = null;
  private baseConfig: FilterConfiguration;
  private viewType: 'customer' | 'prospect' | 'custom' = 'customer';

  private constructor() {
    // Default configuration
    this.baseConfig = {
      filters: DEFAULT_CUSTOMER_FILTERS,
      categories: [
        { id: 'general', label: 'Generella', order: 10 },
        { id: 'segments', label: 'Segment', order: 20 },
        { id: 'activity', label: 'Aktivitet', order: 30 }
      ]
    };
  }

  public static getInstance(): FilterService {
    if (!FilterService.instance) {
      FilterService.instance = new FilterService();
    }
    return FilterService.instance;
  }

  // Initialize with the appropriate base configuration
  public setViewType(viewType: 'customer' | 'prospect' | 'custom'): void {
    this.viewType = viewType;
    
    // Set the base configuration based on view type
    if (viewType === 'prospect') {
      this.baseConfig = {
        filters: PROSPECT_SUSPECT_FILTERS,
        categories: [
          { id: 'prospects', label: 'Prospects', order: 10 },
          { id: 'suspects', label: 'Suspects', order: 20 }
        ]
      };
    } else if (viewType === 'customer') {
      this.baseConfig = {
        filters: DEFAULT_CUSTOMER_FILTERS,
        categories: [
          { id: 'general', label: 'Generella', order: 10 },
          { id: 'segments', label: 'Segment', order: 20 },
          { id: 'activity', label: 'Aktivitet', order: 30 }
        ]
      };
    }
  }

  // Load a custom configuration from an external source
  public loadConfiguration(config: FilterConfiguration): void {
    this.userConfig = config;
  }

  // Reset to default configuration
  public resetConfiguration(): void {
    this.userConfig = null;
  }

  // Get the effective configuration (merged base + user config)
  public getEffectiveConfiguration(): FilterConfiguration {
    if (!this.userConfig) {
      return this.baseConfig;
    }

    // Merge filters
    const mergedFilters = [...this.baseConfig.filters];
    
    // Update or add user filters
    this.userConfig.filters.forEach(userFilter => {
      const existingIndex = mergedFilters.findIndex(f => f.id === userFilter.id);
      
      if (existingIndex >= 0) {
        // Update existing filter
        mergedFilters[existingIndex] = {
          ...mergedFilters[existingIndex],
          ...userFilter
        };
      } else {
        // Add new filter
        mergedFilters.push(userFilter);
      }
    });

    // Merge categories
    const mergedCategories = [...(this.baseConfig.categories || [])];
    
    if (this.userConfig.categories) {
      this.userConfig.categories.forEach(userCategory => {
        const existingIndex = mergedCategories.findIndex(c => c.id === userCategory.id);
        
        if (existingIndex >= 0) {
          // Update existing category
          mergedCategories[existingIndex] = {
            ...mergedCategories[existingIndex],
            ...userCategory
          };
        } else {
          // Add new category
          mergedCategories.push(userCategory);
        }
      });
    }

    return {
      filters: mergedFilters,
      categories: mergedCategories
    };
  }

  // Get all filter definitions, sorted by order
  public getFilters(): FilterDefinition[] {
    const config = this.getEffectiveConfiguration();
    return config.filters
      .filter(filter => filter.display?.visibility !== false)
      .sort((a, b) => {
        const orderA = a.display?.order || 100;
        const orderB = b.display?.order || 100;
        return orderA - orderB;
      });
  }

  // Get filters by category
  public getFiltersByCategory(): Record<string, FilterDefinition[]> {
    const config = this.getEffectiveConfiguration();
    const result: Record<string, FilterDefinition[]> = {};
    
    // Create entries for all categories
    config.categories?.forEach(category => {
      result[category.id] = [];
    });
    
    // Add "other" category for filters without a category
    result.other = [];
    
    // Sort filters into categories
    config.filters
      .filter(filter => filter.display?.visibility !== false)
      .forEach(filter => {
        const category = filter.display?.category || 'other';
        if (!result[category]) {
          result[category] = [];
        }
        result[category].push(filter);
      });
    
    // Sort filters within each category
    Object.keys(result).forEach(category => {
      result[category].sort((a, b) => {
        const orderA = a.display?.order || 100;
        const orderB = b.display?.order || 100;
        return orderA - orderB;
      });
    });
    
    return result;
  }

  // Get categories in sorted order
  public getCategories() {
    const config = this.getEffectiveConfiguration();
    return (config.categories || [])
      .sort((a, b) => a.order - b.order);
  }

  // Convert filter definition to format expected by components
  public getComponentFilter(filter: FilterDefinition): {
    id: string;
    label: string;
    iconComponent: ElementType;
    description: string;
    filters: FilterDefinition['filters'];
  } {
    return {
      id: filter.id,
      label: filter.label,
      iconComponent: ICON_MAP[filter.iconName] || FilterIcon, // Fallback to generic icon
      description: filter.description,
      filters: filter.filters
    };
  }

  // Load user configuration from API or local storage
  public async loadUserConfiguration(userId: string): Promise<void> {
    try {
      // Try to load from localStorage first (for demo/dev)
      const localConfig = localStorage.getItem(`filter_config_${userId}`);
      if (localConfig) {
        this.userConfig = JSON.parse(localConfig);
        return;
      }
      
      // Otherwise load from API
      // const response = await fetch(`/api/filter-config/${userId}`);
      // if (response.ok) {
      //   this.userConfig = await response.json();
      // }
    } catch (error) {
      console.error('Failed to load user filter configuration:', error);
    }
  }

  // Save user configuration to API or local storage
  public async saveUserConfiguration(userId: string, config: FilterConfiguration): Promise<void> {
    try {
      // Save to localStorage (for demo/dev)
      localStorage.setItem(`filter_config_${userId}`, JSON.stringify(config));
      
      // In production, would save to API
      // await fetch(`/api/filter-config/${userId}`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify(config)
      // });
      
      this.userConfig = config;
    } catch (error) {
      console.error('Failed to save user filter configuration:', error);
    }
  }
}

// Export a singleton instance
export const filterService = FilterService.getInstance();
